import React, { useEffect, useRef, useState } from 'react'
import { Gem } from 'lucide-react'
import useAudioManager from '../hooks/useAudioManager'

/**
 * Компонент круговой рулетки для апгрейда
 * @param {Object} targetItem - Целевой скин
 * @param {number} chance - Вероятность успеха (0-100)
 * @param {boolean} isSpinning - Флаг вращения
 * @param {boolean} result - Результат (true = успех, false = неудача)
 */
const UpgradeWheel = ({ targetItem, chance, isSpinning, result }) => {
  const [rotation, setRotation] = useState(0)
  const [countdown, setCountdown] = useState(3)
  const [showCountdown, setShowCountdown] = useState(true)
  const [isExploding, setIsExploding] = useState(false)
  const wheelRef = useRef(null)
  const finalAngleRef = useRef(null)
  const hasStartedSpinning = useRef(false)
  const timerRef = useRef(null)
  const countdownStartedRef = useRef(false)
  const soundPlayedRef = useRef(false) // Флаг для предотвращения повторного воспроизведения
  
  // Инициализация Audio Manager через глобальный audioService
  const { playSound } = useAudioManager({
    timer: 'timer',
    upgraderSpin: 'upgraderSpin'
  })

  // Звуки теперь управляются через useAudioManager (Web Audio API)
  // Предзагрузка происходит автоматически в хуке

  // Логика таймера обратного отсчета
  useEffect(() => {
    if (!isSpinning || !showCountdown) {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      countdownStartedRef.current = false
      soundPlayedRef.current = false // Сбрасываем флаг звука
      return
    }

    if (countdownStartedRef.current) {
      return
    }

    countdownStartedRef.current = true

    // Воспроизводим звук таймера ОДИН РАЗ в начале
    // (в v4_timer_3-2-1-0.mp3 уже есть все три щелчка + взрыв)
    if (!soundPlayedRef.current) {
      playSound('timer')
      soundPlayedRef.current = true
    }

    let currentCount = 3
    let tickCount = 0
    const startTime = Date.now()
    
    setCountdown(3)
    
    const tick = () => {
      tickCount++
      const elapsed = Date.now() - startTime
      const expectedTime = tickCount * 1000
      const drift = elapsed - expectedTime
      
      currentCount--
      setCountdown(currentCount)
      
      // Убрали playSound('tick') - звук воспроизводится один раз в начале
      
      if (currentCount <= 0) {
        setIsExploding(true)
        
        // Убрали playSound('explosion') - взрыв уже в составе timer звука
        
        setTimeout(() => {
          setShowCountdown(false)
          setIsExploding(false)
          hasStartedSpinning.current = true
          
          // Воспроизводим звук вращения колеса
          playSound('upgraderSpin')
        }, 0)
      } else {
        const nextDelay = 1000 - drift
        timerRef.current = setTimeout(tick, Math.max(0, nextDelay))
      }
    }
    
    timerRef.current = setTimeout(tick, 1000)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      countdownStartedRef.current = false
    }
  }, [isSpinning, showCountdown]) // Убрали playSound из зависимостей

  useEffect(() => {
    if (!isSpinning) {
      // Сброс вращения при остановке
      setRotation(0)
      finalAngleRef.current = null
      setCountdown(3)
      setShowCountdown(true)
      setIsExploding(false)
      hasStartedSpinning.current = false
      countdownStartedRef.current = false
      soundPlayedRef.current = false // Сбрасываем флаг звука
      // Очищаем таймер
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      return
    }

    if (finalAngleRef.current === null && hasStartedSpinning.current) {
      const greenZoneAngle = (chance / 100) * 360
      const FULL_ROTATIONS = 5  // Ровно 5 полных оборотов для точности
      const EDGE_MARGIN = 1  // Минимальный отступ от края зоны в градусах
      
      let targetAngle
      if (result !== null) {
        if (result) {
          // Победа - рандомное место в зеленой зоне с отступом от краев
          // Зеленая зона: 0° - greenZoneAngle
          const minAngle = EDGE_MARGIN
          const maxAngle = greenZoneAngle - EDGE_MARGIN
          targetAngle = minAngle + Math.random() * (maxAngle - minAngle)
          console.log('✅ ПОБЕДА! Зеленая зона: 0° - ' + greenZoneAngle + '°, целевой угол: ' + targetAngle.toFixed(2) + '°')
        } else {
          // Проигрыш - рандомное место в красной зоне с отступом от краев
          // Красная зона: greenZoneAngle - 360°
          const minAngle = greenZoneAngle + EDGE_MARGIN
          const maxAngle = 360 - EDGE_MARGIN
          targetAngle = minAngle + Math.random() * (maxAngle - minAngle)
          console.log('❌ ПРОИГРЫШ! Красная зона: ' + greenZoneAngle + '° - 360°, целевой угол: ' + targetAngle.toFixed(2) + '°')
        }
      } else {
        // Рандом (не должно использоваться, но на всякий случай)
        targetAngle = Math.random() * 360
        console.log('🎲 РАНДОМ! Целевой угол: ' + targetAngle.toFixed(2) + '°')
      }
      
      // Вращаем ПО часовой стрелке (положительное значение)
      // Стрелка указывает вверх (0°), зеленая зона тоже начинается сверху
      const totalRotation = FULL_ROTATIONS * 360 + targetAngle
      
      console.log('🌀 Общее вращение: ' + totalRotation.toFixed(2) + '° (' + FULL_ROTATIONS + ' оборотов + ' + targetAngle.toFixed(2) + '°)')
      
      finalAngleRef.current = targetAngle
      setRotation(totalRotation)
    }
  }, [isSpinning, result, chance, hasStartedSpinning.current])

  // Расчет размера зеленой зоны в градусах
  const greenZoneDegrees = (chance / 100) * 360

  return (
    <div className="relative w-full max-w-sm mx-auto">
      {/* Круг с рулеткой */}
      <div className="relative aspect-square">
        {/* Круг с прогрессом */}
        <svg 
          ref={wheelRef}
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 200 200"
        >
          {/* Красная зона (фон) */}
          <circle
            cx="100"
            cy="100"
            r="90"
            fill="none"
            stroke="#ef4444"
            strokeWidth="20"
            className="opacity-50"
          />
          
          {/* Зеленая зона (успех) */}
          <circle
            cx="100"
            cy="100"
            r="90"
            fill="none"
            stroke="#00FF7F"
            strokeWidth="20"
            strokeDasharray={`${(greenZoneDegrees / 360) * 565.48} 565.48`}
            strokeDashoffset="0"
            transform="rotate(-90 100 100)"
            className="transition-all duration-300"
          />
        </svg>

        {/* Центральный круг с таймером или процентом */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-dark-primary rounded-full w-3/5 h-3/5 flex flex-col items-center justify-center border-4 border-surface">
            {showCountdown ? (
              // Показываем таймер
              <div className="text-8xl font-bold text-accent-primary relative" style={{ minHeight: '100px' }}>
                {isExploding ? (
                  // Эффект взрыва
                  <span 
                    className="inline-block"
                    style={{ 
                      animation: 'scatterExplode 600ms ease-out forwards',
                      opacity: 0
                    }}
                  >
                    0
                  </span>
                ) : (
                  // Обычный таймер
                  <span 
                    className="inline-block"
                    style={{
                      animation: countdown <= 1 ? 'fastBlink 200ms infinite' : 'none'
                    }}
                  >
                    {countdown}
                  </span>
                )}
              </div>
            ) : (
              // Показываем изображение целевого скина
              <div className="relative w-full h-full flex flex-col items-center justify-center p-4">
                <div className="w-full h-3/5 flex items-center justify-center mb-2">
                  <img 
                    src={targetItem.img} 
                    alt={targetItem.name}
                    className="max-w-full max-h-full object-contain drop-shadow-2xl"
                  />
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white mb-1">
                    {Math.round(chance)}%
                  </p>
                  {/* <p className="text-[10px] text-text-secondary">
                    Вероятность успеха
                  </p> */}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Стрелка указатель - вращается вокруг центра круга */}
        <div 
          className="absolute inset-0 z-10"
          style={{
            transform: `rotate(${rotation}deg)`,
            // transition: isSpinning ? 'transform 7000ms cubic-bezier(0.25, 0.1, 0.25, 1)' : 'none',
            transition: isSpinning ? 'transform 7000ms cubic-bezier(0.1, 0.25, 0.25, 1)' : 'none',
          }}
        >
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2">
            <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-t-[24px] border-l-transparent border-r-transparent border-t-accent-primary drop-shadow-lg"></div>
          </div>
        </div>
      </div>

      {/* Название целевого скина */}
      <div className="mt-6 text-center">
        <h4 className="text-lg font-semibold text-white mb-1">
          {targetItem.name.includes('|') ? (
            <>
              <div>{targetItem.name.split('|')[0].trim()}</div>
              <div className="text-sm text-text-secondary">
                {targetItem.name.split('|')[1].trim()}
              </div>
            </>
          ) : (
            targetItem.name
          )}
        </h4>
        <p className="text-accent-primary font-bold flex items-center justify-center gap-1">
          <Gem size={16} />
          {targetItem.price.toLocaleString()}
        </p>
      </div>
    </div>
  )
}

export default UpgradeWheel
